<template>
    <div class="password-new-body" v-loading.fullscreen.lock="is_loading">
        <div class="password-new-area">
            <h4 class="content-sub-title">{{ t('attributes.password_new_title') }}</h4>
            <!-- パスワード再設定 パスワード入力 説明書き -->
            <div class="password-new-description" v-html="t('attributes.password_new_description')" />
            <div class="annotation-block annotation-block-lineheight">
                {{ t('attributes.password_explanation1') }}<br />
                {{ t('attributes.password_explanation2') }}
            </div>

            <div class="password-new-form">
                <el-form
                    @submit.native.prevent="onReset"
                    ref="password_form_ref"
                    :rules="rules"
                    :model="password_form"
                    >
                    <el-form-item :label="t('attributes.new_password')" prop="new_password">
                        <el-input
                            class="input-text-free"
                            clearable
                            v-model="password_form.new_password"
                            ref="new_password"
                        />
                    </el-form-item>

                    <el-form-item :label="t('attributes.re_enter')" prop="re_enter">
                        <el-input
                            class="input-text-free"
                            clearable
                            v-model="password_form.re_enter"
                            ref="re_enter"
                        />
                    </el-form-item>
                </el-form>

                <!-- 10文字以上で設定 -->
                <el-row class="flex" :key="'is_length_valid_' + is_length_valid">
                    <span :class="is_length_valid ? 'green-area' : 'red-area'">
                        <i v-if="is_length_valid" class="far fa-check-circle" aria-hidden="true"></i>
                        <i v-else class="far fa-times-circle" aria-hidden="true"></i>
                        {{ t('attributes.password_length') }}
                    </span>
                </el-row>

                <!-- 英大文字と小文字を使用 -->
                <el-row class="flex" :key="'password_requires_upper_and_lower_case_' + password_requires_upper_and_lower_case">
                    <div :class="password_requires_upper_and_lower_case ? 'green-area' : 'red-area'">
                        <i v-if="password_requires_upper_and_lower_case" class="far fa-check-circle" aria-hidden="true"></i>
                        <i v-else class="far fa-times-circle" aria-hidden="true"></i>
                        {{ t('attributes.password_requires_upper_and_lower_case') }}
                    </div>
                </el-row>

                <!-- 数字と記号を使用 -->
                <el-row class="flex" :key="'password_requires_number_and_specialchar_' + password_requires_number_and_specialchar">
                    <div :class="password_requires_number_and_specialchar ? 'green-area' : 'red-area'">
                        <i v-if="password_requires_number_and_specialchar" class="far fa-check-circle" aria-hidden="true"></i>
                        <i v-else class="far fa-times-circle" aria-hidden="true"></i>
                        {{ t('attributes.password_requires_number_and_specialchar') }}
                    </div>
                </el-row>
            </div>

            <!-- 送信orログインページに戻るボタン -->
            <div class="button-block">
                <el-button type="primary" @click="onReset">{{ t('attributes.send') }}</el-button>
                <el-button @click="onBack">{{ t('attributes.back_to_login') }}</el-button>
            </div>
        </div>
    </div>
</template>

<script>
import {
    defineComponent,
    reactive,
    ref,
    computed,
    getCurrentInstance
} from 'vue';
import axios from 'axios';

export default defineComponent({
    setup() {
        // getCurrentInstance から proxy（= this）を取得
        const internal = getCurrentInstance();
        const proxy = internal?.proxy || {};

        const t = proxy?.$t?.bind(proxy);
        const router = proxy?.$router;
        const route = proxy?.$route;

        const is_loading = ref(false);

        // パスワードフォームの初期値
        const password_form = reactive({
            new_password: '',
            re_enter: ''
        });

        const password_form_ref = ref(null);

        // パスワードの長さが10文字以上50文字以下であること
        const is_length_valid = computed(() => {
            const password = password_form.new_password;
            return password.length >= 10 && password.length <= 50;
        });

        // 英大文字と小文字を使用
        const password_requires_upper_and_lower_case = computed(() => {
            const password = password_form.new_password;
            return /[a-z]/.test(password) && /[A-Z]/.test(password);
        });

        // 数字と記号を使用
        const password_requires_number_and_specialchar = computed(() => {
            const password = password_form.new_password;
            return /[0-9]/.test(password) && /[-!"#$%&()*,./:;?@[\\\]^_`{|}~+<=>]/.test(password);
        });

        // パスワードが条件を満たすかチェック
        const passwordFormat = (rule, value, callback) => {
            const password = password_form.new_password;
            if (!password.length) {
                callback();
            } else if (is_length_valid.value && password_requires_upper_and_lower_case.value && password_requires_number_and_specialchar.value) {
                callback();
            } else {
                callback(new Error(t('attributes.password_format_error')));
            }
        };

        // パスワードの再入力が一致するかチェック
        const isMatchPassword = (rule, value, callback) => {
            if (password_form.new_password === password_form.re_enter) {
                password_form_ref.value?.clearValidate([
                    'new_password',
                    're_enter'
                ]);
                callback();
            } else {
                callback(new Error(t('attributes.mis_match_password_error')));
            }
        };

        // バリデーションルールの定義
        const rules = {
            new_password: [
                {
                    validator: passwordFormat,
                    trigger: 'blur'
                }
            ],
            re_enter: [
                {
                    validator: isMatchPassword,
                    trigger: 'blur'
                }
            ]
        };

        // パスワード再設定
        const onReset = () => {
            password_form_ref.value.validate((valid) => {
                if (valid) {
                    is_loading.value = true;
                    const params = { ...password_form };
                    axios
                        .post(`/api/password/reissue/reset/${route.params.token}`, params)
                        .then(() => {
                            proxy?.$message({
                                type: 'success',
                                showClose: true,
                                message: t('attributes.other.update_succeeded')
                            });
                            password_form_ref.value.resetFields();
                        })
                        .catch((error) => {
                            proxy?.$message({
                                type: 'error',
                                showClose: true,
                                message: error.response?.data?.message
                            });
                        })
                        .finally(() => {
                            is_loading.value = false;
                        });
                }
            });
        };

        // ログインページに戻る
        const onBack = () => {
            router.push({ name: 'login' });
        };

        return {
            t,
            is_loading,
            password_form,
            password_form_ref,
            is_length_valid,
            password_requires_upper_and_lower_case,
            password_requires_number_and_specialchar,
            rules,
            onReset,
            onBack
        };
    }
});
</script>
